import { useState, useEffect, useCallback } from 'react';
import { pagamentosApi } from '../api/api';

interface WebhookConfig {
  webhookUrl: string;
  chaveSecreta: string;
  valorPlano: number;
  nomeProduto: string;
  descricaoProduto: string;
  updatedAt: string | null;
}

interface WebhookLog {
  id: number;
  evento: string;
  payload: object;
  statusProcessamento: string;
  mensagemRetorno: string | null;
  createdAt: string;
}

interface Pagamento {
  id: number;
  usuarioId: number;
  nomeCompleto: string;
  email: string;
  paymentId: string | null;
  preferenceId: string | null;
  tipoPagamento: string | null;
  status: string;
  valor: number | null;
  dataAprovacao: string | null;
  createdAt: string;
}

type Tab = 'config' | 'pagamentos' | 'logs';

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    approved: 'bg-emerald-500/20 text-emerald-400',
    pending: 'bg-yellow-500/20 text-yellow-400',
    in_process: 'bg-blue-500/20 text-blue-400',
    rejected: 'bg-red-500/20 text-red-500',
    cancelled: 'bg-gray-500/20 text-gray-400',
    refunded: 'bg-orange-500/20 text-orange-400',
    charged_back: 'bg-red-500/20 text-red-500',
    ok: 'bg-emerald-500/20 text-emerald-400',
    erro: 'bg-red-500/20 text-red-500',
    ignorado: 'bg-gray-500/20 text-gray-400',
  };
  const cls = map[status] ?? 'bg-gray-500/20 text-gray-400';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handle}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
        copied
          ? 'bg-emerald-500/20 text-emerald-400'
          : 'bg-gray-100 text-gray-500 hover:bg-gray-100'
      }`}
    >
      {copied ? (
        <>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copiado
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {label ?? 'Copiar'}
        </>
      )}
    </button>
  );
}

// ─── Tab: Configuração ────────────────────────────────────────────────────────

function ConfigTab() {
  const [config, setConfig] = useState<WebhookConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState('');
  const [valorInput, setValorInput] = useState('');
  const [savingValor, setSavingValor] = useState(false);
  const [valorSalvo, setValorSalvo] = useState(false);
  const [nomeInput, setNomeInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [savingProduto, setSavingProduto] = useState(false);
  const [produtoSalvo, setProdutoSalvo] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await pagamentosApi.getWebhookConfig();
      setConfig(data);
      setValorInput(Number(data.valorPlano).toFixed(2).replace('.', ','));
      setNomeInput(data.nomeProduto ?? '');
      setDescInput(data.descricaoProduto ?? '');
    } catch {
      setError('Erro ao carregar configurações do webhook.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSalvarValor = async () => {
    const val = parseFloat(valorInput.replace(',', '.'));
    if (isNaN(val) || val <= 0) {
      setError('Informe um valor válido maior que zero.');
      return;
    }
    setSavingValor(true);
    setError('');
    try {
      const { data } = await pagamentosApi.atualizarValorPlano(val);
      setConfig((prev) => prev ? { ...prev, valorPlano: data.valorPlano } : prev);
      setValorSalvo(true);
      setTimeout(() => setValorSalvo(false), 2500);
    } catch {
      setError('Erro ao salvar valor do plano.');
    } finally {
      setSavingValor(false);
    }
  };

  const handleSalvarProduto = async () => {
    if (!nomeInput.trim()) {
      setError('Nome do produto é obrigatório.');
      return;
    }
    setSavingProduto(true);
    setError('');
    try {
      await pagamentosApi.atualizarProduto(nomeInput.trim(), descInput.trim());
      setConfig((prev) => prev ? { ...prev, nomeProduto: nomeInput.trim(), descricaoProduto: descInput.trim() } : prev);
      setProdutoSalvo(true);
      setTimeout(() => setProdutoSalvo(false), 2500);
    } catch {
      setError('Erro ao salvar informações do produto.');
    } finally {
      setSavingProduto(false);
    }
  };

  const handleGerarNovaChave = async () => {
    if (!window.confirm('Gerar nova chave vai invalidar a chave atual. Confirma?')) return;
    setGenerating(true);
    try {
      const { data } = await pagamentosApi.gerarNovaChave();
      setConfig((prev) => prev ? { ...prev, chaveSecreta: data.chaveSecreta } : prev);
    } catch {
      setError('Erro ao gerar nova chave.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg className="w-6 h-6 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-500">{error}</div>
      )}

      {/* Valor do plano */}
      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Valor do Plano</p>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Valor cobrado dos usuários ao assinar. Aplicado imediatamente em novos pagamentos.
        </p>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2.5 flex-1">
            <span className="text-xs text-gray-300">R$</span>
            <input
              type="text"
              value={valorInput}
              onChange={(e) => setValorInput(e.target.value.replace(/[^0-9,.]/, ''))}
              className="flex-1 bg-transparent text-sm font-bold text-gray-900 focus:outline-none"
              placeholder="99,90"
            />
          </div>
          <button
            onClick={handleSalvarValor}
            disabled={savingValor}
            className={`px-4 py-2.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
              valorSalvo
                ? 'bg-emerald-500 text-white'
                : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
            } disabled:opacity-50`}
          >
            {savingValor ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : valorSalvo ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Salvo!
              </>
            ) : (
              'Salvar'
            )}
          </button>
        </div>
        {config && (
          <p className="text-xs text-gray-300 mt-2">
            Valor atual: R$ {Number(config.valorPlano).toFixed(2).replace('.', ',')}
          </p>
        )}
      </div>

      {/* Nome e descrição do produto */}
      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-blue-400" />
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Produto / Marca</p>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Nome e descrição exibidos na tela de pagamento do usuário.
        </p>
        <div className="space-y-2 mb-3">
          <input
            type="text"
            value={nomeInput}
            onChange={(e) => setNomeInput(e.target.value)}
            placeholder="Ex: RootFy Code"
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-bold text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
          <input
            type="text"
            value={descInput}
            onChange={(e) => setDescInput(e.target.value)}
            placeholder="Ex: Acesso completo por 30 dias"
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
        </div>
        <button
          onClick={handleSalvarProduto}
          disabled={savingProduto}
          className={`px-4 py-2.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
            produtoSalvo
              ? 'bg-blue-500 text-white'
              : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
          } disabled:opacity-50`}
        >
          {savingProduto ? (
            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : produtoSalvo ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Salvo!
            </>
          ) : (
            'Salvar produto'
          )}
        </button>
      </div>

      {/* URL do webhook */}
      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">URL do Webhook</p>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Configure esta URL no painel do Mercado Pago em Configurações → IPN/Webhooks
        </p>
        <div className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2.5">
          <code className="text-xs text-emerald-300 flex-1 break-all">{config?.webhookUrl}</code>
          {config?.webhookUrl && <CopyButton value={config.webhookUrl} />}
        </div>
      </div>

      {/* Chave secreta */}
      <div className="bg-white border border-gray-100 rounded-xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-yellow-400" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assinatura Secreta</p>
          </div>
          <button
            onClick={() => setShowKey((v) => !v)}
            className="text-xs text-gray-300 hover:text-gray-600 transition-colors"
          >
            {showKey ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Usada para validar a autenticidade das notificações recebidas do Mercado Pago
        </p>
        <div className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2.5 mb-3">
          <code className="text-xs text-yellow-300 flex-1 break-all font-mono">
            {showKey ? config?.chaveSecreta : '••••••••••••••••••••••••••••••••'}
          </code>
          {config?.chaveSecreta && <CopyButton value={config.chaveSecreta} label="Copiar chave" />}
        </div>
        <button
          onClick={handleGerarNovaChave}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-lg text-xs font-medium transition-all"
        >
          {generating ? (
            <>
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Gerando...
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Gerar nova chave
            </>
          )}
        </button>
        {config?.updatedAt && (
          <p className="text-xs text-gray-300 mt-2">
            Última atualização: {new Date(config.updatedAt).toLocaleString('pt-BR')}
          </p>
        )}
      </div>

      {/* Instruções */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
        <p className="text-xs font-semibold text-blue-400 mb-2">Como configurar no Mercado Pago</p>
        <ol className="space-y-1.5 text-xs text-gray-400 list-decimal list-inside">
          <li>Acesse seu painel do Mercado Pago</li>
          <li>Vá em Configurações → Notificações IPN</li>
          <li>Cole a URL do webhook acima</li>
          <li>Selecione o evento "Pagamentos"</li>
          <li>Salve as configurações</li>
          <li>Use a assinatura secreta para validar as notificações recebidas</li>
        </ol>
      </div>
    </div>
  );
}

// ─── Tab: Pagamentos ──────────────────────────────────────────────────────────

function PagamentosTab() {
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    pagamentosApi
      .listarPagamentos()
      .then(({ data }) => setPagamentos(data))
      .catch(() => setError('Erro ao carregar pagamentos.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg className="w-6 h-6 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-500 mb-4">{error}</div>
      )}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-300">{pagamentos.length} transações</p>
      </div>
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        {pagamentos.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">Nenhum pagamento registrado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-gray-300 font-medium">Usuário</th>
                  <th className="text-left px-4 py-3 text-gray-300 font-medium">Método</th>
                  <th className="text-left px-4 py-3 text-gray-300 font-medium">Valor</th>
                  <th className="text-left px-4 py-3 text-gray-300 font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-gray-300 font-medium">Aprovado em</th>
                  <th className="text-left px-4 py-3 text-gray-300 font-medium">Criado em</th>
                </tr>
              </thead>
              <tbody>
                {pagamentos.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="text-gray-700 font-medium">{p.nomeCompleto}</p>
                      <p className="text-gray-400">{p.email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-400 capitalize">{p.tipoPagamento ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {p.valor ? `R$ ${Number(p.valor).toFixed(2).replace('.', ',')}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {p.dataAprovacao
                        ? new Date(p.dataAprovacao).toLocaleString('pt-BR')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {new Date(p.createdAt).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Logs ────────────────────────────────────────────────────────────────

function LogsTab() {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const limit = 20;

  const load = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const { data } = await pagamentosApi.getWebhookLogs(p, limit);
      setLogs(data.logs);
      setTotal(data.total);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-300">{total} eventos registrados</p>
        <button
          onClick={() => load(page)}
          className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-gray-600 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Atualizar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <svg className="w-6 h-6 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          {logs.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">Nenhum log registrado</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {logs.map((log) => (
                <div key={log.id}>
                  <button
                    onClick={() => setExpanded((v) => (v === log.id ? null : log.id))}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left transition-colors"
                  >
                    <StatusBadge status={log.statusProcessamento} />
                    <span className="text-xs text-gray-500 font-mono">{log.evento}</span>
                    <span className="text-xs text-gray-400 ml-auto">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </span>
                    <svg
                      className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded === log.id ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expanded === log.id && (
                    <div className="px-4 pb-4 space-y-2">
                      {log.mensagemRetorno && (
                        <p className="text-xs text-gray-400 bg-black/20 rounded-lg px-3 py-2">
                          {log.mensagemRetorno}
                        </p>
                      )}
                      <div className="bg-black/30 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1 font-medium">Payload</p>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-100 disabled:opacity-30 text-gray-500 rounded-lg text-xs transition-all"
          >
            Anterior
          </button>
          <span className="text-xs text-gray-300">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-100 disabled:opacity-30 text-gray-500 rounded-lg text-xs transition-all"
          >
            Próximo
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function WebhookAdminPage() {
  const [tab, setTab] = useState<Tab>('config');

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    {
      id: 'config',
      label: 'Configuração',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      id: 'pagamentos',
      label: 'Pagamentos',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      id: 'logs',
      label: 'Logs do Webhook',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Pagamentos & Webhook</h1>
        <p className="text-sm text-gray-400 mt-1">
          Área exclusiva para administradores · Mercado Pago
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${ tab === t.id ? 'bg-white text-gray-900 shadow-sm border border-gray-200' : 'text-gray-500 hover:text-gray-700' }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div
        className={tab === 'config' || tab === 'logs' ? 'bg-white border border-gray-200 rounded-2xl p-6' : ''}
      >
        {tab === 'config' && <ConfigTab />}
        {tab === 'pagamentos' && <PagamentosTab />}
        {tab === 'logs' && <LogsTab />}
      </div>
    </div>
  );
}
